/**
 * Centralized date/time utilities for handling timezones
 * Implements the standard approach used by popular apps
 */

/**
 * Converts a date to UTC timestamp for database storage
 * This ensures consistent storage regardless of user's timezone
 * @param {Date|string} date - The date to convert
 * @returns {Date} - Date object in UTC
 */
export const toUTCDate = (date) => {
  const d = new Date(date);
  // Don't convert, just preserve as is - the Date object represents same moment in time
  // regardless of timezone, we'll handle timezone display in toLocalDate
  return new Date(d.getTime());
};

/**
 * Formats a UTC date for display in the user's local timezone
 * @param {Date|string|firebase.firestore.Timestamp} date - The date to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string in user's locale
 */
export const toLocalDate = (date, options = {}) => {
  const d = new Date(date);
  
  if (isNaN(d.getTime())) {
    return 'Invalid date';
  }

  const defaultOptions = {
    // Include timezone in the formatted string
    timeZoneName: 'short',
    ...options
  };

  return d.toLocaleString(undefined, defaultOptions);
};

/**
 * Formats a UTC date as a date string (no time) in the user's timezone
 * @param {Date|string|firebase.firestore.Timestamp} date - The date to format
 * @returns {string} - Formatted date string
 */
export const toLocalDateString = (date, options = {}) => {
  const d = new Date(date);
  
  if (isNaN(d.getTime())) {
    return 'Invalid date';
  }

  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  });
};

/**
 * Formats a UTC date as a time string in the user's timezone
 * @param {Date|string|firebase.firestore.Timestamp} date - The date to format
 * @returns {string} - Formatted time string
 */
export const toLocalTimeString = (date, options = {}) => {
  const d = new Date(date);
  
  if (isNaN(d.getTime())) {
    return 'Invalid date';
  }

  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...options
  });
};

/**
 * Converts a local datetime-local input value to UTC Date
 * HTML datetime-local inputs return date in user's local timezone
 * We need to convert to UTC for storage
 * @param {string} localDateTime - Value from datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns {Date} - Date in UTC
 */
export const toUTCFromLocalInput = (localDateTime) => {
  if (!localDateTime) return new Date();
  
  // Create a Date object from the input
  // The input is in user's local timezone, but Date constructor treats it as local
  const localDate = new Date(localDateTime);
  
  // Return the same Date object - Date internally stores UTC time
  return localDate;
};

/**
 * Gets the current UTC timestamp for database storage
 * @returns {Date} - Current date in UTC
 */
export const getCurrentUTCDate = () => {
  return new Date();
};

/**
 * Converts a Firestore Timestamp to local date string
 * @param {firebase.firestore.Timestamp} timestamp - Firestore Timestamp
 * @returns {Date} - Local Date object
 */
export const fromFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return new Date();
  // Firestore timestamp toDate() already returns a JavaScript Date in UTC
  return timestamp.toDate();
};

/**
 * Formats a datetime-local input value for display
 * @param {string} localDateTime - Value from datetime-local input
 * @returns {string} - Formatted date in user's locale
 */
export const formatLocalDateTime = (localDateTime) => {
  const date = toUTCFromLocalInput(localDateTime);
  return toLocalDate(date);
};

/**
 * Checks if two dates are the same day in the user's timezone
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} - Whether dates are the same day
 */
export const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

/**
 * Gets the user's timezone offset in hours
 * @returns {number} - Offset from UTC in hours
 */
export const getUserTimezoneOffset = () => {
  return -new Date().getTimezoneOffset() / 60;
};

/**
 * Gets the user's timezone name
 * @returns {string} - Timezone name from Intl API
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Converts a Date object to a datetime-local input string in user's local timezone
 * @param {Date} date - The date to convert
 * @returns {string} - String in format YYYY-MM-DDTHH:mm for datetime-local input
 */
export const toLocalInputString = (date = new Date()) => {
  const d = new Date(date);
  
  // Get local date components
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};
